import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useUI } from '@/store/ui'
import { useSelection } from '@/store/selection'
import { ApiError } from '@/api/client'
import { GraphAPI, type GraphNode } from '@/api/graph'
import { DomainsAPI } from '@/api/domains'
import { ENTITY_TYPES, RELATION_TYPES } from '@/lib/graphColors'
import './graph-editor.css'

// Real graph editor — add stars/relations into a chosen domain (POST /graph/entities,
// /graph/relations), then refetch. Entity/relation vocabulary follows the selected domain.
//
// No "world" control: world was an FI-era taxonomy (fund/fi/vc/…) that leaked the founder's
// private clustering into every user's editor. The backend never required it (it was only an
// optional `attributes.world`), so nothing is sent — a new star's glow falls back to the
// neutral default exactly like spring-ingested nodes, and colour comes from the domain layer.

/** What the relation comboboxes need to know about a star. */
export interface NodeOption {
  id: string
  name: string
  type: string
  domain: string
  self?: boolean
}

/**
 * Search-as-you-type over the cached graph nodes: prefix matches outrank substring
 * matches, capped so a 2,000-star sky never renders a 2,000-row dropdown.
 * Empty query = first `limit` nodes (browse posture). Pure — unit-tested.
 */
export function filterNodeOptions<N extends NodeOption>(nodes: N[], query: string, limit = 24): N[] {
  const needle = query.trim().toLowerCase()
  if (!needle) return nodes.slice(0, limit)
  const starts: N[] = []
  const contains: N[] = []
  for (const n of nodes) {
    const name = n.name.toLowerCase()
    if (name.startsWith(needle)) {
      starts.push(n)
      if (starts.length >= limit) break
    } else if (name.includes(needle) && contains.length < limit) {
      contains.push(n)
    }
  }
  return [...starts, ...contains].slice(0, limit)
}

interface NodeComboboxProps {
  label: string
  placeholder: string
  nodes: GraphNode[]
  value: GraphNode | null
  onChange: (node: GraphNode | null) => void
}

/**
 * Accessible combobox over the constellation — mirrors CommandPalette's listbox ARIA
 * (role=combobox input + aria-activedescendant over role=option rows). Typing filters
 * by name; ↑/↓ move, Enter picks, Esc reverts to the committed value.
 */
function NodeCombobox({ label, placeholder, nodes, value, onChange }: NodeComboboxProps) {
  const listId = useId()
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLUListElement>(null)
  const [query, setQuery] = useState(value?.name ?? '')
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)

  // A committed value arriving from outside (pre-fill from the selected star) owns the text —
  // but ONLY when a value arrives. Typing over a pick emits onChange(null); syncing on that
  // null transition would wipe the keystroke the user just typed (adversarial-review catch).
  const valueId = value?.id ?? null
  useEffect(() => {
    if (value) setQuery(value.name)
  }, [valueId])

  const results = useMemo(() => filterNodeOptions(nodes, query), [nodes, query])

  useEffect(() => {
    setActive(0)
  }, [query])

  // Keep the active row visible while arrowing through a scrolled list.
  useEffect(() => {
    if (!open) return
    listRef.current?.querySelector<HTMLElement>('[data-active="true"]')?.scrollIntoView({ block: 'nearest' })
  }, [active, open])

  function pick(n: GraphNode) {
    onChange(n)
    setQuery(n.name)
    setOpen(false)
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        if (!open) setOpen(true)
        else if (results.length) setActive((i) => (i + 1) % results.length)
        break
      case 'ArrowUp':
        e.preventDefault()
        if (!open) setOpen(true)
        else if (results.length) setActive((i) => (i - 1 + results.length) % results.length)
        break
      case 'Enter': {
        if (!open) break
        e.preventDefault()
        const n = results[active]
        if (n) pick(n)
        break
      }
      case 'Escape':
        if (!open) break
        e.preventDefault()
        setOpen(false)
        setQuery(value?.name ?? '')
        break
      case 'Tab':
        setOpen(false)
        break
    }
  }

  const activeId = open && results[active] ? `${listId}-opt-${results[active].id}` : undefined

  return (
    <div className="dw-nodebox">
      <input
        ref={inputRef}
        className="dw-input"
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-activedescendant={activeId}
        aria-autocomplete="list"
        aria-label={label}
        placeholder={placeholder}
        value={query}
        autoComplete="off"
        spellCheck={false}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setQuery(e.target.value)
          setOpen(true)
          // The text no longer names the committed star — the choice is stale.
          if (value && e.target.value !== value.name) onChange(null)
        }}
        onKeyDown={onKeyDown}
        onBlur={() => {
          setOpen(false)
          // Re-show the committed pick; abandoned half-typed text reverts.
          setQuery(value?.name ?? '')
        }}
      />
      {open && (
        <ul className="dw-nodebox-list" role="listbox" id={listId} aria-label={label} ref={listRef}>
          {results.length === 0 && <li className="dw-nodebox-empty">No star answers to that name.</li>}
          {results.map((n, i) => {
            const isActive = i === active
            return (
              <li
                key={n.id}
                id={`${listId}-opt-${n.id}`}
                role="option"
                aria-selected={isActive}
                data-active={isActive}
                className={`dw-nodebox-item${isActive ? ' dw-nodebox-item--active' : ''}`}
                onMouseMove={() => setActive(i)}
                // mousedown (not click) so the pick lands before the input's blur closes the list
                onMouseDown={(e) => {
                  e.preventDefault()
                  pick(n)
                }}
              >
                <span className="dw-nodebox-item-name">{n.name}</span>
                <span className="dw-nodebox-item-hint">{n.self ? 'you' : `${n.type} · ${n.domain}`}</span>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

type Feedback = { kind: 'ok' | 'err'; text: string }

function FeedbackLine({ msg }: { msg: Feedback | null }) {
  if (!msg) return null
  return (
    <div
      className={`dw-ged-msg dw-ged-msg--${msg.kind}`}
      role={msg.kind === 'err' ? 'alert' : 'status'}
    >
      {msg.text}
    </div>
  )
}

export function GraphEditor() {
  const { diwaUrl } = useUI()
  const api = useMemo(() => new GraphAPI(diwaUrl), [diwaUrl])
  const domainsApi = useMemo(() => new DomainsAPI(diwaUrl), [diwaUrl])
  const qc = useQueryClient()
  const { selected, select } = useSelection()
  const { data } = useQuery({ queryKey: ['graph-network'], queryFn: () => api.network() })
  const { data: domainsData } = useQuery({ queryKey: ['domains'], queryFn: () => domainsApi.list() })
  const entities = data?.nodes ?? []
  const userDomains = domainsData?.domains ?? []

  const [domain, setDomain] = useState('')
  const sel = userDomains.find((d) => d.slug === domain)
  const entityOpts = sel?.entity_types ?? [...ENTITY_TYPES]
  const relOpts = sel?.relation_types ?? [...RELATION_TYPES]

  const [name, setName] = useState('')
  const [type, setType] = useState<string>('person')
  const [fromNode, setFromNode] = useState<GraphNode | null>(null)
  const [toNode, setToNode] = useState<GraphNode | null>(null)
  const [rel, setRel] = useState<string>('knows')
  const [busy, setBusy] = useState(false)
  const [nodeMsg, setNodeMsg] = useState<Feedback | null>(null)
  const [edgeMsg, setEdgeMsg] = useState<Feedback | null>(null)

  function onDomainChange(next: string) {
    setDomain(next)
    const d = userDomains.find((dd) => dd.slug === next)
    const nextEntity = d?.entity_types ?? [...ENTITY_TYPES]
    const nextRel = d?.relation_types ?? [...RELATION_TYPES]
    if (nextEntity[0]) setType(nextEntity[0])
    if (nextRel[0]) setRel(nextRel[0])
  }

  // Land on a real region: default to the user's first domain, and recover if the
  // chosen one disappears (deleted in another tab). Never posts into a phantom slug.
  useEffect(() => {
    if (userDomains.length === 0) return
    if (!userDomains.some((d) => d.slug === domain)) onDomainChange(userDomains[0].slug)
  }, [userDomains, domain])

  // Pre-fill "from" with the star selected in the sky — once per selection, so a
  // manual combobox choice isn't clobbered by a refetch.
  const appliedSelRef = useRef<string | null>(null)
  useEffect(() => {
    if (!selected || selected.id === appliedSelRef.current) return
    const n = entities.find((e) => e.id === selected.id)
    if (n) {
      setFromNode(n)
      appliedSelRef.current = selected.id
    }
  }, [selected, entities])

  async function addNode() {
    const trimmed = name.trim()
    if (!trimmed) return
    setBusy(true)
    setNodeMsg(null)
    try {
      const created = await api.createEntity({ type, name: trimmed, domain })
      setName('')
      await qc.invalidateQueries({ queryKey: ['graph-network'] })
      // Success is spatial: the new star becomes the current selection, so the sky
      // and the Dossier tab focus it immediately.
      select({ id: created.id, name: trimmed, type, world: null, score: null, domain, attributes: {} })
      setNodeMsg({ kind: 'ok', text: `“${trimmed}” added — selected in the sky.` })
    } catch (err) {
      const detail = err instanceof ApiError ? err.detail : undefined
      setNodeMsg({ kind: 'err', text: detail ?? 'Could not add the star — try again.' })
    } finally {
      setBusy(false)
    }
  }

  async function addEdge() {
    if (!fromNode || !toNode) {
      setEdgeMsg({ kind: 'err', text: 'Pick both ends of the relation.' })
      return
    }
    if (fromNode.id === toNode.id) {
      setEdgeMsg({ kind: 'err', text: 'Pick two different stars.' })
      return
    }
    setBusy(true)
    setEdgeMsg(null)
    try {
      await api.createRelation({ from_id: fromNode.id, to_id: toNode.id, type: rel, domain })
      await qc.invalidateQueries({ queryKey: ['graph-network'] })
      setEdgeMsg({ kind: 'ok', text: `Linked ${fromNode.name} → ${toNode.name}.` })
      setToNode(null)
    } catch (err) {
      const detail = err instanceof ApiError ? err.detail : undefined
      setEdgeMsg({ kind: 'err', text: detail ?? 'Could not add the relation — try again.' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      <div className="dw-rail-section-title">Add to graph</div>

      <div className="dw-ged-form" style={{ marginBottom: 6 }}>
        <label className="dw-ged-field">
          domain
          <select className="dw-input" value={domain} onChange={(e) => onDomainChange(e.target.value)}>
            {userDomains.map((d) => (
              <option key={d.id} value={d.slug}>{d.name}</option>
            ))}
          </select>
        </label>
      </div>

      <div className="dw-ged-form">
        <input
          className="dw-input"
          placeholder="Name"
          aria-label="New star name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <select className="dw-input" aria-label="Star type" value={type} onChange={(e) => setType(e.target.value)}>
          {entityOpts.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
        <button className="dw-btn" disabled={busy || !name.trim()} onClick={addNode}>+ Star</button>
        <FeedbackLine msg={nodeMsg} />
      </div>

      <div style={{ height: 12 }} />

      <div className="dw-ged-form">
        <NodeCombobox
          label="Relation from"
          placeholder="from — search your stars…"
          nodes={entities}
          value={fromNode}
          onChange={setFromNode}
        />
        <select className="dw-input" aria-label="Relation type" value={rel} onChange={(e) => setRel(e.target.value)}>
          {relOpts.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
        <NodeCombobox
          label="Relation to"
          placeholder="to — search your stars…"
          nodes={entities}
          value={toNode}
          onChange={setToNode}
        />
        <button className="dw-btn" disabled={busy || !fromNode || !toNode} onClick={addEdge}>+ Relation</button>
        <FeedbackLine msg={edgeMsg} />
      </div>
    </div>
  )
}
